import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { Context } from "../../Reducers/Store";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { describeCronExpression } from "../../utils/cronExpression";
import { errorMessage, formatDateTime, formatDuration, methodClass } from "../../utils/formatters";

function Kpi({ label, value, tone = "slate", hint, onClick }) {
  const color = {
    slate: "text-slate-950",
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
    sky: "text-sky-700",
  }[tone];

  const Component = onClick ? "button" : "div";

  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm ${onClick ? "transition hover:border-violet-200 hover:bg-violet-50/40" : ""}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
      {hint && <p className="mt-2 text-sm text-slate-500">{hint}</p>}
    </Component>
  );
}

function scheduleDetail(job) {
  if (job.tipoAgendamento === "Cron") {
    return describeCronExpression(job.expressaoCron);
  }

  return job.horario || "-";
}

function JobRow({ job, onExecute, emExecucao }) {
  return (
    <div className="grid gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 lg:grid-cols-[1fr_120px_150px_120px] lg:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">{job.nome}</p>
          <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${methodClass(job.metodoHttp)}`}>{job.metodoHttp}</span>
          <StatusBadge value={job.ativo ? "Ativo" : "Inativo"} />
          {emExecucao && <StatusBadge value="EmExecucao" />}
        </div>
        <p className="mt-1 truncate text-xs text-slate-500">{job.url}</p>
      </div>
      <p className="text-sm text-slate-600">{job.tipoAgendamento}</p>
      <p className="text-sm text-slate-600">{scheduleDetail(job)}</p>
      <button
        type="button"
        disabled={!job.podeExecutarManualmente || emExecucao}
        onClick={() => onExecute(job)}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m8 5 11 7-11 7V5Z" />
        </svg>
        {emExecucao ? "Rodando" : "Rodar"}
      </button>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const [jobs, setJobs] = useState([]);
  const [execucoes, setExecucoes] = useState([]);
  const [erro, setErro] = useState("");
  const isAdmin = state.profile?.perfil === "Administrador";

  const carregar = useCallback(() => {
    setErro("");
    Promise.all([
      axios.get(apiUrl("/jobs"), { metadata: { ignoreLoading: true } }),
      axios.get(apiUrl("/execucoes/recentes"), { params: { limite: 8 }, metadata: { ignoreLoading: true } }),
    ])
      .then(([jobsResponse, execucoesResponse]) => {
        setJobs(getPayload(jobsResponse) || []);
        setExecucoes(getPayload(execucoesResponse) || []);
      })
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar o dashboard.")));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!execucoes.some((execucao) => execucao.status === "EmExecucao")) return undefined;

    const interval = window.setInterval(carregar, 3000);
    return () => window.clearInterval(interval);
  }, [carregar, execucoes]);

  const kpis = useMemo(() => {
    const ativos = jobs.filter((job) => job.ativo).length;
    const executaveis = jobs.filter((job) => job.podeExecutarManualmente).length;
    const falhas = execucoes.filter((execucao) =>
      execucao.status === "Falha" ||
      execucao.status === "ExpiradoPorTempo"
    ).length;
    const sucesso = execucoes.filter((execucao) => execucao.status === "Sucesso").length;

    return { ativos, executaveis, falhas, sucesso };
  }, [execucoes, jobs]);

  const jobsEmExecucao = useMemo(() => {
    return new Set(
      execucoes
        .filter((execucao) => execucao.status === "EmExecucao")
        .map((execucao) => execucao.jobAgendadoId)
    );
  }, [execucoes]);

  const executarJob = (job) => {
    axios
      .post(apiUrl(`/jobs/${job.id}/executar`))
      .then((response) => {
        const execucao = getPayload(response);
        if (execucao) {
          setExecucoes((current) => [
            execucao,
            ...current.filter((item) => item.id !== execucao.id),
          ].slice(0, 8));
        }
      })
      .catch((error) => setErro(errorMessage(error, "Não foi possível executar o job.")));
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Dashboard"
        subtitle="Visão rápida das tarefas agendadas e das últimas execuções."
        actions={
          isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/jobs")}
              className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
              </svg>
              Novo job
            </button>
          )
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Jobs visíveis" value={jobs.length} hint="Respeita permissões do usuário." onClick={() => navigate("/jobs")} />
        <Kpi label="Jobs ativos" value={kpis.ativos} tone="emerald" />
        <Kpi label="Execução manual" value={kpis.executaveis} tone="sky" hint="Liberados para seu usuário." />
        <Kpi label="Falhas recentes" value={kpis.falhas} tone={kpis.falhas ? "red" : "slate"} onClick={() => navigate("/execucoes")} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,.8fr)]">
        <Panel title="Jobs disponíveis" subtitle="Ação manual aparece apenas para jobs permitidos.">
          {jobs.length === 0 ? (
            <EmptyState title="Nenhum job disponível." subtitle={isAdmin ? "Cadastre o primeiro job para iniciar o agendamento." : "Peça liberação de acesso a um administrador."} />
          ) : (
            <div>
              {jobs.slice(0, 6).map((job) => (
                <JobRow key={job.id} job={job} onExecute={executarJob} emExecucao={jobsEmExecucao.has(job.id)} />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Últimas execuções" subtitle={`${kpis.sucesso} com sucesso nesta amostra.`}>
          {execucoes.length === 0 ? (
            <EmptyState title="Sem execuções recentes." />
          ) : (
            <div className="divide-y divide-slate-100">
              {execucoes.map((execucao) => (
                <button
                  type="button"
                  key={execucao.id}
                  onClick={() => navigate("/execucoes")}
                  className="block w-full px-4 py-3 text-left transition hover:bg-slate-50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-sm font-semibold text-slate-900">{execucao.nomeJob}</p>
                    <StatusBadge value={execucao.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>{formatDateTime(execucao.iniciadaEm)}</span>
                    <span>{formatDuration(execucao.duracaoMs)}</span>
                    {execucao.codigoHttp && <span>HTTP {execucao.codigoHttp}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
